import { GameConfig } from "./GameConfig/GameConfig.js";
import Player from "./Player/player.js";
import InputHandler from "./Input/InputHandler.js";
import { EnemyStateConfig } from "./GameConfig/EnemyConfig.js";
import Turret from "./Pickups/turret.js";

import { PickupAbility } from "./GameConfig/PickupConfig.js";
import { PickUpUI, ScoreUI, BulletUI, GameOverUI } from "./UI/index.js";
import { setupParallaxLayers } from "./Parallax/Parallax.js";
import { EnemyGenerator } from "./Enemy/EnemyGenerator.js";
import PickupGenerator from "./Pickups/PickupGenerator.js";

window.addEventListener("load", () => {
  const loading = document.getElementById("loading");
  loading.remove();

  const canvas = document.getElementById("game-canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = GameConfig.CANVAS_WIDTH;
  canvas.height = GameConfig.CANVAS_HEIGHT;

  startGameUI();

  class Game {
    constructor() {
      this.gameWidth = GameConfig.CANVAS_WIDTH;
      this.gameHeight = GameConfig.CANVAS_HEIGHT;

      this.player = new Player(this);

      this.allParallaxLayers = setupParallaxLayers(
        GameConfig.CANVAS_WIDTH,
        GameConfig.CANVAS_HEIGHT,
        GameConfig.GAME_SPEED,
      );

      this.keys = new Set();
      this.inputHandler = new InputHandler(this);

      this.enemies = [];
      this.enemyGenerationTimer = 0;
      this.enemyGenerationInterval = GameConfig.ENEMY_GENERATION_INTERVAL_MS;

      this.playerWeaponProjectiles = [];

      this.pickups = [];
      this.pickupTimer = 0;
      this.pickupGenerationInterval = GameConfig.PICKUP_GENERATION_INTERVAL_MS;

      this.gameOver = false;
      this.score = 0;

      this.totalAmmo = GameConfig.TOTAL_AMMO;
      this.currentAmmo = this.totalAmmo;
      this.ammoTimer = 0;
      this.ammoInterval = GameConfig.AMMO_INTERVAL_MS;

      this.scoreUI = new ScoreUI();
      this.pickUpUI = new PickUpUI();
      this.bulletUI = new BulletUI();
    }

    draw(deltaTime) {
      ctx.clearRect(0, 0, this.gameWidth, this.gameHeight);

      this.enemyGenerationTimer += deltaTime;
      if (this.enemyGenerationTimer > this.enemyGenerationInterval) {
        EnemyGenerator(this.enemies);
        this.enemyGenerationTimer = 0;
      }
      this.pickupTimer += deltaTime;
      if (this.pickupTimer > this.pickupGenerationInterval) {
        this.pickupTimer = 0;
        this.pickups.push(PickupGenerator());
      }

      this.allParallaxLayers.forEach((layer) => {
        layer.draw(ctx);
      });

      this.scoreUI.draw(ctx);
      this.pickUpUI.draw(ctx);
      this.bulletUI.draw(ctx);

      this.player.draw(ctx, deltaTime);

      this.pickups.forEach((pickup) => {
        pickup.draw(ctx);
      });

      this.enemies.forEach((enemy) => {
        enemy.draw(ctx, deltaTime);
      });

      this.playerWeaponProjectiles.forEach((projectile) => {
        projectile.draw(ctx, deltaTime);
      });
    }

    update(deltaTime) {
      this.scoreUI.update(this.score);
      this.pickUpUI.update(this.player.ability);
      this.bulletUI.update(this.currentAmmo);

      this.allParallaxLayers.forEach((layer) => {
        layer.update(GameConfig.GAME_SPEED);
      });

      if (this.ammoTimer > this.ammoInterval) {
        if (this.currentAmmo < this.totalAmmo) {
          this.currentAmmo += 1;
        }
        this.ammoTimer = 0;
      } else {
        this.ammoTimer += deltaTime;
      }

      this.player.update(this.keys);

      this.pickups.forEach((pickup, index) => {
        pickup.update(deltaTime, this.player);
        if (this.checkCollision(pickup, this.player)) {
          this.player.ability.add(pickup.ability);
          pickup.markedForDeletion = true;
          // this.player.pickUpAddOns.add(
          //   new ShieldAddOn(PlayerPickupConfig.SHIELD.IMG_SRC),
          // );
        }
        if (pickup.markedForDeletion) {
          this.pickups.slice(index, 1);
          index--;
        }
      });

      this.enemies.forEach((enemy, index) => {
        enemy.update(deltaTime);
        if (enemy.markedForDeletion) {
          this.enemies.splice(index, 1);
          index--;
        }

        if (
          enemy.isColliding &&
          !this.player.ability.has(PickupAbility.SHIELD) &&
          this.checkCollision(enemy, this.player)
        ) {
          enemy.setState(EnemyStateConfig.DEATH);

          this.player.health -= enemy.damage;

          if (this.player.health <= 0) {
            this.gameOver = true;
          }
        }

        enemy.projectiles.forEach((projectile, ind) => {
          projectile.update();

          if (
            !this.player.ability.has(PickupAbility.SHIELD) &&
            this.checkCollision(projectile, this.player)
          ) {
            projectile.markedForDeletion = true;
            this.player.health -= projectile.damage;
          }
          if (projectile.markedForDeletion) {
            enemy.projectiles.splice(ind, 1);
            ind--;
          }
        });

        this.playerWeaponProjectiles.forEach((projectile, ind) => {
          projectile.update();

          if (enemy.isColliding && this.checkCollision(enemy, projectile)) {
            enemy.health -= projectile.damage;

            projectile.markedForDeletion = true;
            if (enemy.health <= 0) {
              enemy.setState(EnemyStateConfig.DEATH);
              this.score += enemy.score;
            }
          }

          if (projectile.markedForDeletion) {
            this.playerWeaponProjectiles.splice(ind, 1);
            ind--;
          }
        });
      });
    }
    checkCollision(enemy, object) {
      // const dx = enemy.x - object.x;
      // const dy = enemy.y - object.y;
      // const distance = Math.sqrt(dx * dx + dy * dy);
      //
      // return distance < enemy.spriteWidth - object.spriteWidth;

      // rectangular object collision
      return (
        enemy.x < object.x + object.spriteHeight &&
        enemy.x + enemy.spriteHeight > object.x &&
        enemy.y < object.y + object.spriteHeight &&
        enemy.y + enemy.spriteHeight > object.y
      );
    }

    drawUI() {
      this.gameUI.draw(ctx, this.player.ability);
    }
  }

  const game = new Game();

  // game.pickups.push(new Shield());
  // game.pickups.push(new Turret());

  let lastTime = 0;
  function animate(timeStamp) {
    const deltaTime = timeStamp - lastTime;
    lastTime = timeStamp;
    game.draw(deltaTime);
    game.update(deltaTime);

    if (game.gameOver) {
      GameOverUI(game.score);
    }
    if (!game.gameOver) requestAnimationFrame(animate);
  }

  function startGameUI() {
    const startBtn = document.getElementById("start");
    const restartBtn = document.getElementById("restart");

    startBtn.addEventListener("click", () => {
      startBtn.hidden = true;
      animate(0);
    });
    restartBtn.addEventListener("click", () => {
      game.gameOver = false;
      restartBtn.hidden = true;

      const gameOverUI = document.getElementById("gameOver");
      gameOverUI.hidden = true;

      animate(0);
    });
  }
});
